/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState} from 'react'
import axios from 'axios'
import {API_URL} from '../../../modules/auth/core/_requests'
import {useAuth} from '../../../modules/auth'
import AIOptions, {AIOptionValues, defaultAIOptionValues} from './components/AIOptions'

type CollectionType = 'BRAND' | 'CATEGORY'

export const CollectionWriterPage: FC = () => {
  const {currentUser} = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  // Wizard step state:
  // 1 = Input, 2 = Preview, 3 = AI Rewrite, 4 = Final Check & Push
  const [step, setStep] = useState<number>(1)

  // Tab state for collection type – default is now "CATEGORY"
  const [activeTab, setActiveTab] = useState<CollectionType>('CATEGORY')

  // Step 1 input fields (except URL are optional)
  const [collectionUrl, setCollectionUrl] = useState<string>('')
  const [seoKeywords, setSeoKeywords] = useState<string>('')
  const [extraCollectionInfo, setExtraCollectionInfo] = useState<string>('')
  const [additionalAIInstructions, setAdditionalAIInstructions] = useState<string>('')
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)

  // Fetched collection data
  const [fetchedTitle, setFetchedTitle] = useState<string>('')
  const [fetchedDescription, setFetchedDescription] = useState<string>('')

  // AI-generated new description (in HTML)
  const [newDescription, setNewDescription] = useState<string>('')

  // Loading flags
  const [loadingFetch, setLoadingFetch] = useState<boolean>(false)
  const [loadingRewrite, setLoadingRewrite] = useState<boolean>(false)
  const [loadingPush, setLoadingPush] = useState<boolean>(false)

  // Global reset
  const resetWizard = () => {
    setStep(1)
    setCollectionUrl('')
    setSeoKeywords('')
    setExtraCollectionInfo('')
    setAdditionalAIInstructions('')
    setFetchedTitle('')
    setFetchedDescription('')
    setNewDescription('')
  }

  // --- Step 1: Input & Fetch Collection Data ---
  const handleFetchCollection = async () => {
    if (!collectionUrl) {
      alert('Please enter a collection URL.')
      return
    }
    setLoadingFetch(true)
    try {
      let finalUrl = collectionUrl.trim()
      if (!finalUrl.endsWith('.json')) {
        finalUrl += '.json'
      }
      const resp = await axios.post(`${API_URL}/collection_writer/fetch_collection`, {
        sourceUrl: finalUrl,
      })
      if (resp.data && resp.data.collection) {
        const coll = resp.data.collection
        setFetchedTitle(coll.title || '(No title found)')
        // Use the "description" key; if not available, fall back to "body_html"
        setFetchedDescription(coll.description || coll.body_html || '(No description found)')
      } else {
        setFetchedTitle('(No title found)')
        setFetchedDescription('(No description found)')
      }
      setStep(2)
    } catch (err) {
      console.error(err)
      alert('Failed to fetch collection data.')
    } finally {
      setLoadingFetch(false)
    }
  }

  // --- Step 3: Call AI to Rewrite the Description ---
  const handleRewriteWithAI = async () => {
    if (!collectionUrl) {
      alert('Collection URL is required.')
      return
    }
    if (!fetchedTitle && !extraCollectionInfo) {
      alert('A collection title (fetched or extra info) is required.')
      return
    }
    setLoadingRewrite(true)
    try {
      const payload = {
        storeName: currentStoreName, // used for pushing to Shopify
        collectionType: activeTab, // "BRAND" or "CATEGORY"
        originalTitle: fetchedTitle,
        originalDescription: fetchedDescription,
        seoKeywords,
        extraCollectionInfo,
        additionalAIInstructions,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
      }
      const resp = await axios.post(`${API_URL}/collection_writer/process_collection`, payload)
      if (resp.data && resp.data.optimizedDescription) {
        setNewDescription(resp.data.optimizedDescription)
        setStep(4)
      } else {
        alert('No description returned from AI.')
      }
    } catch (err) {
      console.error(err)
      alert('Failed to process collection with AI.')
    } finally {
      setLoadingRewrite(false)
    }
  }

  // --- Step 4: Push the New Description to Shopify ---
  const handlePushToShopify = async () => {
    if (!collectionUrl) {
      alert('Collection URL is required.')
      return
    }
    if (!newDescription) {
      alert('No new description to push.')
      return
    }
    setLoadingPush(true)
    try {
      const payload = {
        storeName: currentStoreName,
        collectionUrl: collectionUrl,
        newDescription: newDescription,
      }
      const resp = await axios.post(`${API_URL}/collection_writer/update_collection`, payload)
      if (resp.data && resp.data.message) {
        alert(resp.data.message)
        resetWizard()
      } else {
        alert('No response from Shopify update.')
      }
    } catch (err) {
      console.error(err)
      alert('Failed to push update to Shopify.')
    } finally {
      setLoadingPush(false)
    }
  }

  // --- Wizard Navigation (Step Circles) ---
  const renderWizardSteps = () => {
    const steps = [
      {num: 1, label: 'Source URL'},
      {num: 2, label: 'Preview'},
      {num: 3, label: 'AI Rewrite'},
      {num: 4, label: 'Push'},
    ]
    return (
      <div className='d-flex justify-content-between mb-4' style={{maxWidth: '600px'}}>
        {steps.map((s) => (
          <div
            key={s.num}
            className={`text-center ${step >= s.num ? 'fw-bold' : 'text-muted'}`}
            style={{width: '25%'}}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: step >= s.num ? '#0d6efd' : '#ccc',
                color: 'white',
                margin: '0 auto',
                lineHeight: '40px',
              }}
            >
              {s.num}
            </div>
            <div style={{fontSize: '0.9rem'}}>{s.label}</div>
          </div>
        ))}
      </div>
    )
  }

  // --- Tab Navigation for Collection Type ---
  const renderTabs = () => (
    <ul className='nav nav-tabs mt-4 px-3'>
      <li className='nav-item'>
        <a
          className={`nav-link ${activeTab === 'CATEGORY' ? 'active' : ''}`}
          onClick={() => setActiveTab('CATEGORY')}
          style={{cursor: 'pointer'}}
        >
          Category Collection
        </a>
      </li>
      <li className='nav-item'>
        <a
          className={`nav-link ${activeTab === 'BRAND' ? 'active' : ''}`}
          onClick={() => setActiveTab('BRAND')}
          style={{cursor: 'pointer'}}
        >
          Brand Collection
        </a>
      </li>
    </ul>
  )

  // --- Step 1: Source Input ---
  const renderStep1 = () => (
    <div className='px-3 py-3'>
      <div className='card mt-3 shadow-sm'>
        <div className='card-header'>
          <h2 className='mb-0'>Step 1: Enter Collection URL & Details</h2>
        </div>
        <div className='card-body'>
          <div className='mb-3'>
            <label className='form-label'>Collection URL</label>
            <input
              type='text'
              className='form-control'
              placeholder='https://yourstore.myshopify.com/collections/collection-handle'
              value={collectionUrl}
              onChange={(e) => setCollectionUrl(e.target.value)}
            />
          </div>
          <div className='mb-3'>
            <label className='form-label'>
              SEO Keywords <span className='text-muted'>(Optional)</span>
            </label>
            <input
              type='text'
              className='form-control'
              placeholder='keyword1, keyword2, keyword3'
              value={seoKeywords}
              onChange={(e) => setSeoKeywords(e.target.value)}
            />
          </div>
          <div className='mb-3'>
            <label className='form-label'>
              Extra Collection Info <span className='text-muted'>(Optional)</span>
            </label>
            <textarea
              className='form-control'
              rows={3}
              placeholder='Enter extra information about the collection...'
              value={extraCollectionInfo}
              onChange={(e) => setExtraCollectionInfo(e.target.value)}
            />
          </div>
          <div className='mb-3'>
            <label className='form-label'>
              Additional AI Instructions <span className='text-muted'>(Optional)</span>
            </label>
            <textarea
              className='form-control'
              rows={3}
              placeholder='Additional instructions to guide the rewrite...'
              value={additionalAIInstructions}
              onChange={(e) => setAdditionalAIInstructions(e.target.value)}
            />
          </div>
          <AIOptions value={aiOptions} onChange={setAiOptions} showPromptExtra={false} />
          <div className='d-flex justify-content-end'>
            <button
              className='btn btn-primary'
              onClick={handleFetchCollection}
              disabled={loadingFetch}
            >
              {loadingFetch ? 'Fetching...' : 'Fetch Collection »'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  // --- Step 2: Preview Collection ---
  const renderStep2 = () => (
    <div className='px-3 py-3'>
      <div className='card mt-3 shadow-sm'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h2 className='mb-0'>Step 2: Preview Collection</h2>
          <button className='btn btn-sm btn-outline-secondary' onClick={() => setStep(1)}>
            « Back
          </button>
        </div>
        <div className='card-body'>
          <h5>Fetched Collection Preview:</h5>
          <p>
            <strong>Title:</strong> {fetchedTitle}
          </p>
          <div>
            <strong>Description:</strong>
            <div
              className='p-2 border rounded'
              style={{maxHeight: '200px', overflowY: 'auto'}}
              dangerouslySetInnerHTML={{__html: fetchedDescription}}
            />
          </div>
          <div className='d-flex justify-content-end mt-3'>
            <button className='btn btn-primary' onClick={() => setStep(3)}>
              Next: AI Rewrite »
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  // --- Step 3: AI Rewrite ---
  const renderStep3 = () => (
    <div className='px-3 py-3'>
      <div className='card mt-3 shadow-sm'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h2 className='mb-0'>Step 3: AI Rewrite</h2>
          <button className='btn btn-sm btn-outline-secondary' onClick={() => setStep(2)}>
            « Back
          </button>
        </div>
        <div className='card-body'>
          <p>
            <strong>Collection Type:</strong>{' '}
            {activeTab === 'BRAND' ? 'Brand Collection' : 'Category Collection'}
          </p>
          <p>
            <strong>Original Title:</strong> {fetchedTitle}
          </p>
          <p>
            <strong>Original Description / Info:</strong>{' '}
            {fetchedDescription !== '(No description found)'
              ? fetchedDescription
              : extraCollectionInfo}
          </p>
          <div className='d-flex justify-content-end mt-3'>
            <button
              className='btn btn-info'
              onClick={handleRewriteWithAI}
              disabled={loadingRewrite}
            >
              {loadingRewrite ? 'Rewriting...' : 'Rewrite with AI »'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  // --- Step 4: Final Check & Push ---
  const renderStep4 = () => (
    <div className='px-3 py-3'>
      <div className='card mt-3 shadow-sm'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h2 className='mb-0'>Step 4: Final Check & Push</h2>
          <button className='btn btn-sm btn-outline-secondary' onClick={() => setStep(3)}>
            « Back
          </button>
        </div>
        <div className='card-body'>
          <div className='mb-3'>
            <label className='form-label fw-bold'>New Description (Editable)</label>
            <textarea
              className='form-control'
              rows={8}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </div>
          <div className='mb-3'>
            <label className='form-label fw-bold'>HTML Preview</label>
            <div
              className='border p-2 bg-light'
              style={{minHeight: 200, maxHeight: 350, overflowY: 'auto'}}
              dangerouslySetInnerHTML={{__html: newDescription}}
            />
          </div>
          <div className='d-flex justify-content-end'>
            <button
              className='btn btn-success'
              onClick={handlePushToShopify}
              disabled={loadingPush}
            >
              {loadingPush ? 'Pushing...' : 'Push to Shopify'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className='container py-3'>
      {/* Top Bar */}
      <div className='d-flex align-items-center justify-content-between mb-3 px-3'>
        <h1>
          Collection Writer – {currentStoreName}
          {collectionUrl && (
            <span className='ms-3 text-muted' style={{fontSize: '0.9rem'}}>
              Source: {collectionUrl}
            </span>
          )}
        </h1>
        <button className='btn btn-warning' onClick={resetWizard}>
          Reset Wizard
        </button>
      </div>

      {/* Tabs */}
      {renderTabs()}

      {/* Wizard Steps */}
      {renderWizardSteps()}

      {/* Render Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  )
}

export const CollectionWriterWrapper: FC = () => {
  return <CollectionWriterPage />
}
